import type { Note } from "../types";

// Wikilink resolution + connection-walking over the loaded vault notes. Shared by
// the note renderer (inline [[links]]) and the quest detail page (its note + the
// notes it connects to). Small on purpose - the vault in memory is a few dozen
// notes, so a linear scan for backlinks is fine.

// Resolve a [[wikilink]] target to a loaded note path, Obsidian-style: an exact
// path match first, then a basename match on any note. null when nothing matches.
export function resolveNotePath(target: string, notes: Record<string, Note> | null): string | null {
  if (!notes) return null;
  const want = target.replace(/\.md$/i, "").trim().toLowerCase();
  if (!want) return null;
  const keys = Object.keys(notes);
  const exact = keys.find((k) => k.replace(/\.md$/i, "").toLowerCase() === want);
  if (exact) return exact;
  return keys.find((k) => (k.replace(/\.md$/i, "").split("/").pop() ?? "").toLowerCase() === want) ?? null;
}

// Every [[wikilink]] target found in a note's blocks (alias stripped, raw text).
const LINK = /\[\[([^\]]+)\]\]/g;
export function noteLinkTargets(note: Note | undefined): string[] {
  if (!note) return [];
  const out: string[] = [];
  for (const b of note.blocks || []) {
    const text = b.text || "";
    LINK.lastIndex = 0;
    for (let m = LINK.exec(text); m; m = LINK.exec(text)) {
      const t = (m[1] || "").split("|")[0]?.trim();
      if (t) out.push(t);
    }
  }
  return out;
}

// Notes connected to `path`: outgoing (it links out to) and backlinks (link in to
// it), each as resolved note paths. Self-links and unresolved targets are dropped.
export function noteConnections(path: string | undefined, notes: Record<string, Note> | null): { outgoing: string[]; backlinks: string[] } {
  if (!path || !notes || !notes[path]) return { outgoing: [], backlinks: [] };
  const outgoing = Array.from(
    new Set(noteLinkTargets(notes[path]).map((t) => resolveNotePath(t, notes)).filter((p): p is string => !!p && p !== path)),
  );
  const backlinks: string[] = [];
  for (const [k, n] of Object.entries(notes)) {
    if (k === path) continue;
    if (noteLinkTargets(n).some((t) => resolveNotePath(t, notes) === path)) backlinks.push(k);
  }
  return { outgoing, backlinks };
}
